import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, UntypedFormBuilder } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import get from 'lodash-es/get';
import isEqual from 'lodash-es/isEqual';
import { Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, finalize, takeUntil } from 'rxjs/operators';

import {
  EntityStatus,
  IOrganisation,
  IUser,
  RolesByGroup,
  UserRole,
  UserRoleGroup,
  UserStatus,
  EntityType,
} from '@portal/entity-services/interfaces';
import { UserAppmarketSettings } from '@portal/marketplace/api-interfaces';
import { FeatureToggle } from '@portal/shared/auth/feature-toggle';
import { LanguageHandle, LanguageOption, LanguageOptions } from '@portal/shared/languages';
import {
  ErrorService,
  FormBase,
  MultilingualValidators,
  RegexPatterns,
  UserValidators,
} from '@portal/shared/ui/form';
import { BaseModalComponent } from '@portal/shared/ui/modal';
import { ISelectInput } from '@portal/shared/ui/filter';
import { UserService } from '@portal/shared/user';
import { UserRoleEditorMatrix } from '../../services/user-role-editor-matrix.service';
import environment from '@environments';
import {
  Application,
  AuthorizationService,
  IUserApps,
  RestrictPermissionService,
  SelfCheckService,
} from '@portal/shared/auth/authorization';
import { Features } from 'environments/enums/features.enum';
import { OrganisationService } from '@portal/entity-services/organisations';
import { ErrorLoggingService } from '@portal/shared/error-handler/src';
import { CountryService } from '@portal/shared/helpers/src';
import { ToastService } from '@portal/shared/ui/toast';
import { MobileViewService } from '@apps/portal/src/app/services/mobile-view.service';
import { CallbackBodyBuilder } from '@portal/shared/user/src/lib/callback-body-builder';
import { NotifierService } from '@portal/shared/ui/notifier';
import { OtpError, OtpErrorMessage } from '../../enums/error-message.enum';
import {
  PhoneOtpAttemptState,
  PhoneOtpAttemptTrackerService,
} from '../../services/phone-otp-attempt-tracker.service';
import { ITermsAndConditions } from '@portal/entity-services/interfaces/src/lib/users/interfaces/terms-and-conditions.interface';
import { TermsAndConditions } from '@portal/shared/ui/layouts/src/lib/enums/terms-and-conditions.enum';
import { CallbackBody } from '@portal/entity-services/interfaces/src/lib/users/interfaces/phone-number-otp.interface';
import { HttpErrorResponse } from '@angular/common/http';
@Component({
  selector: 'portal-form-user',
  templateUrl: './form.component.html',
  standalone: false,
})
export class FormUserComponent extends FormBase<IUser> implements OnInit, OnDestroy {
  @ViewChild('resetPasswordModal') resetPasswordModal: BaseModalComponent;
  @ViewChild('changePasswordModal') changePasswordModal: BaseModalComponent;
  @ViewChild('verifyPhoneNumberModal') verifyPhoneNumberModal: BaseModalComponent;
  @ViewChild('fullNameInput') fullNameInput: ElementRef<HTMLInputElement>;
  @ViewChild('firstNameInput') firstNameInput: ElementRef<HTMLInputElement>;

  @Input() user: IUser;
  @Input() isMyProfile: boolean;
  @Input() isUserNameToCreate: boolean;
  @Input() loggedInUser: IUser;
  @Input() saving = false;

  selfEditMode: boolean;
  roleGroups = [
    UserRoleGroup.Merchant,
    UserRoleGroup.EstateOwner,
    UserRoleGroup.Verifone,
    UserRoleGroup.Developer,
    UserRoleGroup.Provider,
    UserRoleGroup.Reseller,
    UserRoleGroup.Partner,
    UserRoleGroup.Petro,
  ];
  editableRoles: RolesByGroup;
  // True when the Organisation field must be shown read-only because it is (or
  // is being set to) a Group organisation. This applies when an Acquirer Admin
  // creates a user, and when editing any user whose organisation is a Group.
  isOrganisationReadonly = false;
  organisationReadonlyName: string;
  // True when an Acquirer Admin is creating/editing a user: only the Acquirer
  // User role applies, so the shared role picker is replaced with a static,
  // pre-selected and disabled Acquirer User checkbox.
  disableRolePicker = false;
  changePasswordUrl = environment.AUTH.CHANGE_PASSWORD_URL;
  canSwitchLanguage: boolean;
  resetPasswordFeatureActive = false;
  showSendResetPasswordEmailButton = false;
  languagesList: LanguageOption[] = this.langOptions.list;
  hasFormChanged = false;
  initialValue = {} as IUser;
  isInvalid = ErrorService.isFieldInvalid;
  unsubscribe$: Subject<void> = new Subject<void>();
  defaultCountryCode: string;

  isPetroUser = false;
  isPetroSiteAdmin = false;

  apps: IUserApps[] = [];
  appList: ISelectInput[] = [];
  selectedLanguage: LanguageOption;
  hideApplyButton = true;
  notificationUpdateStatus = false;
  isUserNameToUpdate = false;
  isVerifoneEmail: boolean;
  isDeveloperCompany = false;
  isUserInContextFromDeveloperCompany = false;
  isNamewithNewConfig = false;
  federationStatus: boolean;
  isLanguageButtonFocused = false;
  loadingDefaultCountry = false;
  showSendResetPasswordBtn = true;
  currentHostname: string;
  otpFormControl: FormControl;
  marketingTermsAndConditionsFormControl: FormControl;
  isDeviceMobile$: Observable<boolean>;
  authId: string;
  wso2MobileOtpCallbacks: any; // Store WSO2 callbacks for reuse
  isVerifyPhoneNumberButtonLoading: boolean;
  isPhoneNumberOtpLoading: boolean;
  isResendOtpLoading: boolean;
  isInvalidOtpError: boolean;
  showVerifyPhoneNumberBtn = false;

  // OTP attempt tracking (mirrors forgot-password Request New OTP behaviour)
  resendCountdown = 0;
  isResendButtonDisabled = false;
  hasExceededMaxResends = false;

  // When WSO2 authentication is disabled the backend does not drive the OTP
  // resend counter/cooldown, so the resend button is shown as "Resend OTP"
  // without a countdown and is not disabled by the countdown.
  isWSO2Enabled = Boolean(environment.FEATURE_FLAGS.WSO2_AUTHENTICATION);
  hasExceededMaxRetries = false;
  otpErrorMessage: string;

  protected readonly OtpErrorMessage = OtpErrorMessage;
  protected readonly OtpError = OtpError;

  constructor(
    fb: UntypedFormBuilder,
    private userRoleEditMatrix: UserRoleEditorMatrix,
    private userService: UserService,
    private activatedRoute: ActivatedRoute,
    private authorizationService: AuthorizationService,
    private featureToggleService: FeatureToggle,
    private selfCheckService: SelfCheckService,
    private userValidators: UserValidators,
    customValidators: MultilingualValidators,
    public languageService: LanguageHandle,
    protected elementRef: ElementRef,
    private langOptions: LanguageOptions,
    private restrictPermissionService: RestrictPermissionService,
    private organisationService: OrganisationService,
    private errorLoggingService: ErrorLoggingService,
    private countryService: CountryService,
    private toastService: ToastService,
    private mobileViewService: MobileViewService,
    private callbackBodyBuilder: CallbackBodyBuilder,
    private notifierService: NotifierService,
    private phoneOtpTracker: PhoneOtpAttemptTrackerService,
  ) {
    super(
      fb.group(
        {
          userUid: [''],
          email: [
            '',
            [customValidators.email, customValidators.required],
            userValidators.asyncValidateExistingUser(),
          ],
          name: ['', [customValidators.validateCharacters, customValidators.maxLength(62)]],
          nameDetails: fb.group({
            firstName: [
              '',
              [
                customValidators.required,
                customValidators.validateCharacters,
                customValidators.maxLength(20, 'First name'),
              ],
            ],
            middleName: [
              '',
              [customValidators.validateCharacters, customValidators.maxLength(20, 'Middle name')],
            ],
            lastName: [
              '',
              [customValidators.validateCharacters, customValidators.maxLength(20, 'Last name')],
            ],
          }),
          roles: [[], [customValidators.required]],
          status: [EntityStatus.Active, customValidators.required],
          entityUid: ['', [customValidators.required]],
          language: [languageService.getBuildLanguage()],
          defaultApplication: [],
          mobilePhoneNumber: [''],
        },
        elementRef,
      ),
    );

    const rolesByGroup = this.userRoleEditMatrix.getRolesByGroup(
      this.authorizationService.getRoles(),
      this.roleGroups,
    );
    this.editableRoles = this.filterDisabledFeatureRoles(rolesByGroup);

    this.otpFormControl = new FormControl('', [
      customValidators.minLength(6, undefined, OtpErrorMessage.incorrectOtpAlertMessage),
      customValidators.maxLength(6, undefined, OtpErrorMessage.incorrectOtpAlertMessage),
    ]);
    this.marketingTermsAndConditionsFormControl = new FormControl(false);
    this.isDeviceMobile$ = this.mobileViewService.mobileDevice;
    this.isVerifyPhoneNumberButtonLoading = false;
    this.isPhoneNumberOtpLoading = false;
    this.isInvalidOtpError = false;

    this.otpFormControl.valueChanges.subscribe(() => {
      if (this.otpFormControl.valid) {
        this.isInvalidOtpError = false;
        return;
      }
      this.isInvalidOtpError = true;
    });

    this.phoneOtpTracker.state
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((state: PhoneOtpAttemptState) => {
        this.resendCountdown = state.resendCountdown;
        this.isResendButtonDisabled = state.isResendButtonDisabled;
        this.hasExceededMaxResends = state.hasExceededMaxResends;
        this.hasExceededMaxRetries = state.hasExceededMaxRetries;
      });
  }

  ngOnInit(): void {
    this.canSwitchLanguage = this.featureToggleService.isFeatureActive(Features.LanguageSwitching);
    this.showVerifyPhoneNumberBtn = this.featureToggleService.isConfigFeatureActive(
      Features.phoneNumberVerification,
    );
    const permissions = this.restrictPermissionService.getPermissionsForRoute(this.activatedRoute);
    this.selfEditMode = this.selfCheckService.isSelfPage(this.activatedRoute.snapshot);
    this.organisationService
      .getByKey(this.authorizationService.getUserEntityId())
      .subscribe((entity) => {
        this.isUserInContextFromDeveloperCompany = entity.entityType === 'DEVELOPER_COMPANY';
      });
    if (
      this.restrictPermissionService.checkPermissions(permissions, ['app-notification-setting'])
    ) {
      /**
       * This block will be removed in the future after API moved to ES
       */
      this.formValidated.subscribe(() => {
        this.userService
          .setAppMarketUserSettings(
            UserAppmarketSettings.AppUpdateNotification,
            this.notificationUpdateStatus,
          )
          .subscribe();
      });
      this.userService.getAppMarketUserSettings().subscribe((data) => {
        let value = data.data.find(
          (input) => input.name === UserAppmarketSettings.AppUpdateNotification,
        )?.value;

        // according to AC value should be 'true' by default
        if (typeof value === 'undefined') {
          value = 'true';
        }

        this.notificationUpdateStatus = value === 'true';
        this.initialValue.notificationUpdateStatus = this.notificationUpdateStatus;
        this.form.controls['entityUid'].valueChanges.subscribe((entityId) => {
          if (entityId) {
            this.organisationService.getByKey(entityId).subscribe((entity) => {
              this.isDeveloperCompany = entity.entityType === 'DEVELOPER_COMPANY';
            });
          }
        });
      });
      /**
       * End of the block
       */
    }

    if (this.user) {
      this.user.entityUid = get(this.user, 'entity.entityUid', this.user.entityUid);
      this.isDeveloperCompany = this.user.entity?.entityType === 'DEVELOPER_COMPANY';
      this.setFormValue(this.user);
      this.email.disable();
      this.apps = this.getAvailableApps();
      this.appList = this.apps.map(({ name, application }) => ({
        id: application,
        text: name,
      }));
      this.setDefaultApplication();
      this.email.setAsyncValidators(this.userValidators.asyncValidateExistingUser(this.user.email));
      if (this.selfEditMode) {
        this.roles.disable();
        this.entityUid.disable();
      } else {
        this.checkingVerifoneEmail(this.user?.email);
      }
      if (!this.isUserNameToCreate) {
        this.name.disable();
      }
      if (this.user.roles.length === 1 && this.user.roles[0] === UserRole.MerchantUnapproved) {
        this.editableRoles.MERCHANT = this.editableRoles.MERCHANT.filter((role) => {
          return role === UserRole.MerchantAdmin;
        });
      } else {
        this.removeMerchantUnapprovedRole();
      }
      if (this.authorizationService.isApplicationUser(Application.Petro)) {
        this.isPetroUser = true;
        this.isPetroSiteAdmin = this.user.roles.includes(UserRole.PetroSiteAdmin);
        this.name.disable();
        this.language.disable();
      }
      if (this.user.nameDetails && Object.keys(this.user.nameDetails).length > 0) {
        this.isNamewithNewConfig = true;
        this.isUserNameToUpdate = true;
      }
      // Only an Acquirer Admin is restricted from changing a Group user's
      // organisation or elevating their roles. Higher-level admins (e.g.
      // Verifone Admin) must retain full edit access, so gate the constraint on
      // the logged-in user's role rather than the edited user's organisation
      // type.
      if (this.authorizationService.getRoles().includes(UserRole.AcquirerAdmin)) {
        this.applyGroupOrganisationConstraints(this.user.entityUid, false, this.user.entity);
      }
    } else {
      this.language.setValue(
        this.languageService.resolveAlias(this.languageService.getBuildLanguage()),
      );
      this.removeMerchantUnapprovedRole();
      // Decide the Acquirer-User-only restriction synchronously from the
      // creator's token so the picker's Acquirer User checkbox is pre-selected
      // and disabled from the very first render (the picker seeds its checked
      // state at init, before the async organisation lookup below resolves).
      if (this.authorizationService.getRoles().includes(UserRole.AcquirerAdmin)) {
        this.applyAcquirerUserOnlyRole();
      }
      // When the creator belongs to a Group organisation (e.g. an Acquirer
      // Admin) pre-select that group read-only and restrict the roles.
      this.applyGroupOrganisationConstraints(this.authorizationService.getUserEntityId(), true);
    }
    this.initialValue = this.form.value;
    this.initialValue.notificationUpdateStatus = this.notificationUpdateStatus;
    this.form.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => this.updateHasFormChanged());

    this.email.valueChanges.pipe(debounceTime(100)).subscribe((change) => {
      if (change) {
        this.checkingVerifoneEmail(change);
      }
    });

    this.phoneNumberControl.valueChanges.pipe(debounceTime(100)).subscribe((change) => {
      if (change) {
        this.authorizationService.isPhoneNumberVerified = false;
      }
    });

    const lang = this.language.value;
    this.selectedLanguage =
      this.languagesList.find((i) => i.id === lang) ||
      this.languageService.getCurrentObjectLanguage(lang);

    this.setDefaultCountryCode();
    this.resetPasswordFeatureActive = this.featureToggleService.isFeatureActive(
      Features.ChangePassword,
    );
    this.currentHostname = window.location.hostname;
    if (
      (environment.TwoFactorAuthUrls as string[])
        .filter(Boolean)
        .some((url) => this.currentHostname.includes(url))
    ) {
      this.showSendResetPasswordBtn = false;
    }
    this.showSendResetPasswordEmailButton =
      this.user &&
      this.resetPasswordFeatureActive &&
      !this.isMyProfile &&
      !this.federationStatus &&
      this.showSendResetPasswordBtn;
    if (
      this.user?.marketingTermsAndConditions?.hasOwnProperty('status') &&
      this.isPetroSiteAdmin &&
      this.showVerifyPhoneNumberBtn
    ) {
      let isAccepted = false;
      if (
        (this.user.marketingTermsAndConditions as ITermsAndConditions).status ===
        TermsAndConditions.Accepted
      ) {
        isAccepted = true;
      }
      this.initialValue.marketingTermsAndConditions = isAccepted;
      this.marketingTermsAndConditionsFormControl.setValue(isAccepted);
      this.form.addControl(
        'marketingTermsAndConditions',
        this.marketingTermsAndConditionsFormControl,
      );
    }
  }

  setDefaultCountryCode(): void {
    if (window.localStorage.getItem('defaultCountryCode')) {
      this.defaultCountryCode = window.localStorage.getItem('defaultCountryCode');
    } else {
      this.loadingDefaultCountry = true;
      this.organisationService
        .getEntityByID(this.authorizationService.getUserEntityId())
        .pipe(
          catchError((err) => {
            this.errorLoggingService.postErrorLog(err);
            return null;
          }),
          finalize(() => (this.loadingDefaultCountry = false)),
        )
        .subscribe((entity) => {
          if (entity) {
            this.defaultCountryCode = this.countryService.countriesAlpha3.get(
              entity['locale']?.countryCode,
            )?.alpha2;
          }
        });
    }
  }

  onOrganisationSelected(organisation: IOrganisation): void {
    this.federationStatus = organisation?.federationStatus ?? false;

    // Check if this is a GROUP entity with restrictByLabels
    const restrictByLabels = (organisation as any)?.restrictByLabels;
    const acquirerRolesArray = [UserRole.AcquirerAdmin, UserRole.AcquirerUser];
    const currentRoles: UserRole[] = this.roles.value || [];

    if (
      organisation?.entityType === EntityType.GROUP &&
      restrictByLabels &&
      Array.isArray(restrictByLabels) &&
      restrictByLabels.length > 0
    ) {
      // This is an Acquirer group entity - show only Acquirer roles
      this.editableRoles = {
        [UserRoleGroup.Acquirer]: acquirerRolesArray,
      };

      // Keep only the previously selected Acquirer roles
      this.roles.setValue(currentRoles.filter((role) => acquirerRolesArray.includes(role)));
    } else {
      // Reset to default editable roles based on user's permissions
      const rolesByGroup = this.userRoleEditMatrix.getRolesByGroup(
        this.authorizationService.getRoles(),
        this.roleGroups,
      );
      this.editableRoles = this.filterDisabledFeatureRoles(rolesByGroup);
      this.removeMerchantUnapprovedRole();

      // Acquirer roles are only valid for Acquirer group entities and are now
      // hidden, so uncheck any previously selected Acquirer role to keep the
      // form value in sync with what is shown.
      const filteredRoles = currentRoles.filter((role) => !acquirerRolesArray.includes(role));
      if (filteredRoles.length !== currentRoles.length) {
        this.roles.setValue(filteredRoles);
      }
    }
  }

  /**
   * Single place that applies the Group organisation rules for both create and
   * edit. When the resolved entity is a Group organisation the Organisation
   * field is shown read-only, and when it is an Acquirer group (a GROUP with
   * `restrictByLabels`) the role picker is restricted to Acquirer Admin /
   * Acquirer User. In create mode the resolved group is also written into the
   * `entityUid` control; in edit mode the control already holds the value.
   *
   * @param entityUid entity to resolve when `knownEntity` is not provided
   * @param applyToControl when true, set the `entityUid` control to the group
   * @param knownEntity already-loaded entity (avoids an extra request in edit)
   */
  private applyGroupOrganisationConstraints(
    entityUid: string,
    applyToControl: boolean,
    knownEntity?: IOrganisation,
  ): void {
    const applyConstraints = (entity: IOrganisation | null): void => {
      if (!entity || entity.entityType !== EntityType.GROUP) {
        return;
      }
      this.isOrganisationReadonly = true;
      this.organisationReadonlyName = entity.name;

      // An Acquirer group (a GROUP with restrictByLabels) only supports the
      // Acquirer User role here: an Acquirer Admin cannot create or edit another
      // Acquirer Admin, so surface Acquirer User only for both create and edit.
      // Without this the picker is empty in edit because the token-driven
      // editableRoles excludes the Acquirer group.
      const restrictByLabels = (entity as any)?.restrictByLabels;
      if (Array.isArray(restrictByLabels) && restrictByLabels.length > 0) {
        this.applyAcquirerUserOnlyRole();
      }

      if (applyToControl) {
        this.entityUid.setValue(entity.entityUid);
        this.initialValue = this.form.value;
      }
    };

    if (knownEntity) {
      applyConstraints(knownEntity);
      return;
    }
    if (!entityUid) {
      return;
    }
    this.organisationService
      .getByKey(entityUid)
      .pipe(
        catchError(() => of(null)),
        takeUntil(this.unsubscribe$),
      )
      .subscribe((entity: IOrganisation | null) => applyConstraints(entity));
  }

  /**
   * Locks the role picker to Acquirer User only. An Acquirer Admin cannot create
   * or edit another Acquirer Admin, so the picker offers Acquirer User only,
   * defaults it selected, and is disabled since there is no other option.
   */
  private applyAcquirerUserOnlyRole(): void {
    this.editableRoles = {
      [UserRoleGroup.Acquirer]: [UserRole.AcquirerUser],
    };
    this.disableRolePicker = true;
    if (!this.roles.value?.length) {
      this.roles.setValue([UserRole.AcquirerUser]);
    }
  }

  ngOnDestroy(): void {
    this.phoneOtpTracker.reset();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  /**
   * True when a self-editing user has entered a phone number that must still
   * be verified via OTP before their changes can be saved. Only applies when
   * the phone-number-verification feature is enabled and the number has
   * actually changed from what is already saved on the profile, so editing
   * other fields (e.g. name) with an already-verified/unchanged phone is not
   * blocked.
   */
  private get requiresPhoneVerification(): boolean {
    return (
      this.selfEditMode &&
      this.showVerifyPhoneNumberBtn &&
      !!this.phoneNumberControl?.value?.length &&
      this.phoneNumberControl.value !== this.initialValue?.mobilePhoneNumber &&
      !this.authorizationService.isPhoneNumberVerified
    );
  }

  get canSaveUser(): boolean {
    if (!this.user) {
      return true;
    }

    if (this.requiresPhoneVerification) {
      return false;
    }

    return (
      this.hasFormChanged &&
      this.user?.status === UserStatus.Active &&
      (this.authorizationService.hasPermissionToManage(this.user.roles) || this.selfEditMode)
    );
  }

  get showPhoneNumberVerificationInfo(): boolean {
    return (
      this.selfEditMode &&
      this.showVerifyPhoneNumberBtn &&
      this.phoneNumberControl?.value?.length &&
      this.phoneNumberControl.valid &&
      !this.authorizationService.isPhoneNumberVerified
    );
  }

  get showMarketingTermsAndConditions(): boolean {
    const isValid =
      this.isPetroSiteAdmin &&
      this.phoneNumberControl?.value?.length &&
      this.phoneNumberControl.valid &&
      this.authorizationService.isPhoneNumberVerified &&
      this.showVerifyPhoneNumberBtn;
    if (!isValid && this.form.get('marketingTermsAndConditions')) {
      this.form.removeControl('marketingTermsAndConditions');
      this.initialValue.marketingTermsAndConditions = undefined;
    }
    return isValid;
  }

  get isOtpSubmitButtonEnabled(): boolean {
    return (
      this.isPhoneNumberOtpLoading ||
      this.otpFormControl?.value?.length === 0 ||
      this.isInvalidOtpError
    );
  }

  get isVerifyPhoneNumberButtonDisabled(): boolean {
    return (
      (this.isPetroUser && !this.isPetroSiteAdmin) ||
      !this.phoneNumberControl.value ||
      !this.phoneNumberControl.valid ||
      this.authorizationService.isPhoneNumberVerified
    );
  }

  get showPhoneVerificationAlert(): boolean {
    return (
      !this.authorizationService.isPhoneNumberVerified &&
      this.authorizationService
        .getIdTokenPermissions()
        .some((permission) => permission.featureId === 'ALERT_ADD_MOBILE_NO') &&
      this.isPetroSiteAdmin &&
      this.showVerifyPhoneNumberBtn
    );
  }

  get name(): AbstractControl {
    return this.form.get('name');
  }

  get firstName(): AbstractControl {
    return this.form.get('nameDetails.firstName');
  }

  get middleName(): AbstractControl {
    return this.form.get('nameDetails.middleName');
  }

  get lastName(): AbstractControl {
    return this.form.get('nameDetails.lastName');
  }

  get alternateFullName(): AbstractControl {
    return this.form.get('nameDetails.alternateFullName');
  }

  get email(): AbstractControl {
    return this.form.get('email');
  }

  get phoneNumberControl(): AbstractControl {
    return this.form.get('mobilePhoneNumber');
  }

  get roles(): AbstractControl {
    return this.form.get('roles');
  }

  get entityUid(): AbstractControl {
    return this.form.get('entityUid');
  }

  get language(): AbstractControl {
    return this.form.get('language');
  }

  get defaultApplication(): AbstractControl {
    return this.form.get('defaultApplication');
  }

  setIsUserNameToUpdate(): void {
    this.isUserNameToUpdate = true;
  }

  checkFirstName(): void {
    // this.firstName mandatory value, force to enter first name when we update users without first name
    if (!this.isUserNameToCreate && !this.firstName.value && this.canSaveUser) {
      this.isUserNameToUpdate = true;
    }
  }

  onSetRoles(roles: UserRole[]): void {
    this.roles.patchValue(roles);
  }

  toShowDeveloperNotificationSettings(): boolean {
    return (
      (this.isDeveloperCompany && !this.isMyProfile) ||
      (this.isMyProfile && this.isUserInContextFromDeveloperCompany)
    );
  }

  onResetPasswordModalConfirmed(): void {
    const userUid = this.user.userUid;

    this.userService
      .resetPassword({
        data: { userUid },
      })
      .pipe(
        finalize(() => {
          this.resetPasswordModal.close();
        }),
      )
      .subscribe(() => {
        this.toastService.showToast(
          $localize`Reset password link email was sent to ${this.user.name} (${userUid}) successfully`,
        );
      });
  }

  onChangePasswordModalConfirmed(): void {
    // Redirect to change password in the authentication server
    window.location.assign(this.changePasswordUrl);
    this.changePasswordModal.close();
  }

  getAvailableApps(): IUserApps[] {
    const appsPermissions = this.authorizationService.appsPermissions;
    if (appsPermissions?.length) {
      return this.authorizationService.getApps(appsPermissions);
    }

    return [];
  }

  onSelectLanguage(language: LanguageOption): void {
    this.selectedLanguage = language;
    this.language.setValue(this.languageService.resolveAlias(language?.id));
  }

  setDefaultApplication(): void {
    if (this.apps.length < 2) {
      return;
    }

    const savedAppKey = this.userService.defaultApplication || Application.PaymentsPortal;

    if (this.apps.find((item) => item.application === savedAppKey)) {
      this.defaultApplication.setValue(savedAppKey);
    } else {
      this.defaultApplication.setValue(this.apps[0].application);
    }
  }

  onNotificationUpdate(event: Event): void {
    const target = <HTMLInputElement>event.target;
    this.notificationUpdateStatus = target.checked;
    this.updateHasFormChanged();
  }

  updateHasFormChanged(): void {
    this.hasFormChanged = !isEqual(this.initialValue, {
      ...this.form.value,
      ...(typeof this.initialValue.notificationUpdateStatus !== 'undefined'
        ? { notificationUpdateStatus: this.notificationUpdateStatus }
        : null),
    });
  }

  onTabKeyNavigation(): void {
    this.isLanguageButtonFocused = document.activeElement === document.getElementById('langButton');
    if (document.getElementById('langDropdown')?.classList.contains('is-active')) {
      document.querySelectorAll('.langListButton').forEach((elem) => {
        if (elem === document.activeElement) {
          elem.classList.add('has-border-primary');
        } else {
          elem.classList.remove('has-border-primary');
        }
      });
    }
  }

  verifyPhoneNumber(): void {
    const email = this.email?.value;
    const phoneNumber = this.phoneNumberControl?.value;
    this.isVerifyPhoneNumberButtonLoading = true;
    this.resetPhoneNumberValidationModal();

    const generateOtp$ = this.isWSO2Enabled
      ? this.userService.generateOtpWSO2(email, phoneNumber)
      : this.userService.generateOtp(email, phoneNumber);

    generateOtp$
      .pipe(
        catchError((error) => {
          this.handleError(error);
          return of(null);
        }),
        finalize(() => {
          this.isVerifyPhoneNumberButtonLoading = false;
        }),
      )
      .subscribe((response: CallbackBody) => {
        if (response) {
          this.authId = response?.authId;
          // Store WSO2 callbacks if present
          if (this.isWSO2Enabled && response?.callbacks) {
            this.wso2MobileOtpCallbacks = response.callbacks;
          }
          // Reset attempt tracking and start the 30s initial cooldown
          this.phoneOtpTracker.reset();
          this.otpErrorMessage = undefined;
          this.phoneOtpTracker.startInitialCountdown();
          this.phoneOtpTracker.updateFromResponse(response);
          this.verifyPhoneNumberModal.open();
        }
      });
  }

  verifyOtp(): void {
    const verificationCode = this.otpFormControl.value;

    let body: CallbackBody;
    if (this.isWSO2Enabled && this.wso2MobileOtpCallbacks) {
      // WSO2 flow - use CallbackBodyBuilder to create request body
      body = this.callbackBodyBuilder.createWSO2MobileOtpVerificationBody(
        this.authId,
        this.wso2MobileOtpCallbacks,
        verificationCode,
        'submit'
      );
    } else {
      // FRIC flow - use existing method
      body = this.callbackBodyBuilder.createOTPRequestBody(this.authId, verificationCode);
    }

    this.isPhoneNumberOtpLoading = true;

    const verifyOtp$ = this.isWSO2Enabled
      ? this.userService.verifyOtpWSO2(body)
      : this.userService.verifyOtp(body);

    verifyOtp$
      .pipe(
        catchError((error) => {
          this.handleError(error);
          return of(null);
        }),
        finalize(() => {
          this.isPhoneNumberOtpLoading = false;
        }),
      )
      .subscribe((response) => {
        if (response) {
          // Check if response has authId (CallbackBody type)
          if ('authId' in response) {
            this.authId = response.authId;
            // Update stored callbacks if present (for WSO2)
            if (this.isWSO2Enabled && 'callbacks' in response) {
              this.wso2MobileOtpCallbacks = response.callbacks;
            }
          }

          // Check for successful OTP verification (OtpSuccessResponse type)
          if ('tokenId' in response && 'successUrl' in response) {
            this.onOtpVerified();
            this.authorizationService.isPhoneNumberVerified = true;
          } else if (
            'callbacks' in response &&
            response.callbacks?.[0]?.type === 'PasswordCallback' &&
            response.callbacks?.[0]?.output[0]?.value === 'OTP'
          ) {
            // Wrong OTP: honour the remaining-attempts count when the API
            // returns it. When the response does not include remaining
            // attempts (e.g. WSO2 authentication disabled), do NOT assume a
            // default count and decrement it on the front end.
            const hasRemainingAttempts = this.hasServerAttemptStatus(response);
            if (hasRemainingAttempts) {
              this.phoneOtpTracker.updateFromResponse(response);
            }
            this.setOtpErrorMessage(hasRemainingAttempts);
            this.isInvalidOtpError = true;
          }
        }
      });
  }

  resendOtp(): void {
    let body: CallbackBody;
    if (this.isWSO2Enabled && this.wso2MobileOtpCallbacks) {
      // WSO2 flow - use CallbackBodyBuilder to create request body for resend
      body = this.callbackBodyBuilder.createWSO2MobileOtpVerificationBody(
        this.authId,
        this.wso2MobileOtpCallbacks,
        '', // Empty OTP for resend
        'resend'
      );
    } else {
      // FRIC flow - use existing method
      body = this.callbackBodyBuilder.createResendOtpRequestBody(this.authId);
    }

    this.isResendOtpLoading = true;

    // Requesting a new OTP resets the entered code, error and retry counter
    this.otpFormControl?.setValue('');
    this.isInvalidOtpError = false;
    this.otpErrorMessage = undefined;
    this.phoneOtpTracker.resetOtpForm();

    const verifyOtp$ = this.isWSO2Enabled
      ? this.userService.verifyOtpWSO2(body)
      : this.userService.verifyOtp(body);

    verifyOtp$
      .pipe(
        catchError((error) => {
          this.handleError(error);
          return of(null);
        }),
        finalize(() => {
          this.isResendOtpLoading = false;
        }),
      )
      .subscribe((response) => {
        if (response) {
          // Check if response has authId (CallbackBody type)
          if ('authId' in response) {
            this.authId = response.authId;
            // Update stored callbacks if present (for WSO2)
            if (this.isWSO2Enabled && 'callbacks' in response) {
              this.wso2MobileOtpCallbacks = response.callbacks;
            }
          }
          // Honour the server-side resend count when the API returns it.
          // When the API does not return a resend count (e.g. WSO2
          // authentication disabled), do NOT assume a default limit or lockout
          // on the front end. Resends stay allowed (only the 30s cooldown
          // applies) until the backend responds with an error.
          if (this.hasServerAttemptStatus(response)) {
            this.phoneOtpTracker.updateFromResponse(response);
          } else {
            this.phoneOtpTracker.startInitialCountdown();
          }
        }
      });
  }

  private removeMerchantUnapprovedRole(): void {
    this.editableRoles.MERCHANT = this.editableRoles?.MERCHANT?.filter((role) => {
      return role !== UserRole.MerchantUnapproved;
    });
  }

  private filterDisabledFeatureRoles(rolesByGroup: RolesByGroup): RolesByGroup {
    const featureDisabledRoles = [
      {
        name: Features.VHQCore,
        roles: [UserRole.VerifoneHelpdesk],
      },
    ];

    return Object.keys(rolesByGroup).reduce((filteredRolesByGroup, key) => {
      filteredRolesByGroup[key] = rolesByGroup[key].filter((role: UserRole) => {
        return featureDisabledRoles.every((feature) => {
          const isFeatureActive = this.featureToggleService.isFeatureActive(feature.name);
          return isFeatureActive || !feature.roles.includes(role);
        });
      });

      return filteredRolesByGroup;
    }, {});
  }

  private checkingVerifoneEmail(email: string): void {
    this.isVerifoneEmail = RegexPatterns.verifoneEmail.test(email?.toLowerCase());
  }

  private handleError(error: HttpErrorResponse): void {
    this.resetPhoneNumberValidationModal();
    this.phoneOtpTracker.reset();
    this.verifyPhoneNumberModal.close();

    // Account lockout: show the generic "maxed out" message rather than a
    // locked-user message, mirroring the old resend-limit behaviour.
    if (error?.error?.lockedReason === 'MAX_ATTEMPTS_EXCEEDED') {
      this.notifierService.error(OtpErrorMessage[OtpError.RESEND_OTP_LIMIT_EXCEED]);
      return;
    }

    const errorMessage =
      OtpErrorMessage[error?.error?.message || error?.message || ''] ||
      $localize`An unexpected error occurred. Please try again.`;
    this.notifierService.error(errorMessage);
  }

  /**
   * Builds the inline OTP error message shown under the input, based on the
   * current tracker state (remaining retries / max retries reached).
   */
  private setOtpErrorMessage(hasRemainingAttempts: boolean): void {
    // When the API does not return the remaining attempts (e.g. WSO2
    // authentication disabled), clear the message so the template falls back
    // to the generic OtpErrorMessage.incorrectOtpAlertMessage instead of
    // assuming a default count.
    if (!hasRemainingAttempts) {
      this.otpErrorMessage = undefined;
      return;
    }

    const state = this.phoneOtpTracker.currentState;
    if (state.hasExceededMaxRetries) {
      this.otpErrorMessage = $localize`Too many incorrect attempts. Please request a new OTP.`;
      return;
    }
    const retryRemaining = state.retryRemaining;
    const attemptsText = retryRemaining === 1 ? $localize`attempt` : $localize`attempts`;
    this.otpErrorMessage = $localize`Incorrect or expired passcode entered. You have ${retryRemaining} ${attemptsText} left. Try again or request a new passcode.`;
  }

  /**
   * True when the server response carries attempt/resend status so the tracker
   * can be driven by the server instead of local counters.
   */
  private hasServerAttemptStatus(response: any): boolean {
    return (
      response?.retryStatus?.remaining_retries !== undefined ||
      response?.resendStatus?.resend_remaining_count !== undefined
    );
  }

  formatCountdown(seconds: number): string {
    return this.phoneOtpTracker.formatCountdown(seconds);
  }

  private resetPhoneNumberValidationModal(): void {
    this.isInvalidOtpError = false;
    this.otpErrorMessage = undefined;
    this.otpFormControl?.setValue('');
  }

  private onOtpVerified(): void {
    this.verifyPhoneNumberModal.close();
    this.authorizationService.isPhoneNumberVerified = true;
    this.toastService.showToast($localize`Successfully verified phone number`);
    if (this.showMarketingTermsAndConditions) {
      if (!this.form.get('marketingTermsAndConditions')) {
        this.form.addControl(
          'marketingTermsAndConditions',
          this.marketingTermsAndConditionsFormControl,
        );
      }
      this.marketingTermsAndConditionsFormControl.setValue(true);
    }
  }
}
